import UrlHelpers from "./url";
import {FieldHandler} from "./fieldHandler";
import {isDomainValid, isOnlyText} from "./validation";
import {i18n} from "./i18n";
import {MaterialHelpers} from "./materialHelpers";
import * as mdc from "material-components-web";
import {autoInit} from "material-components-web";
import {errorDto} from "./errorHandler";

export class OrderWizard {

    private readonly _i18n: i18n = new i18n();

    private readonly _btnNextPrefix = "btnNext_";
    private _isDomainCheckRunning: boolean = false;
    private _domainCheckTimerMS: number = 750;

    private readonly _isSysAdmin: boolean;
    private readonly _isNewUser: boolean;
    private readonly _isMultiOrgaUser: boolean;

    private _selectedOrganizationId: string;

    private _selectedTenantId: string;
    private _selectedTenantDomain: string;
    private _deselectedTenant: {id: string, domain: string};
    private _selectedTenantListItem: HTMLLIElement;

    private _currentStep: OrderWizardPages;
    private _previousSteps: OrderWizardPages[];

    private _animationActive = false;
    private _debugEnabled: boolean = false;

    private _createOrganizationInputIds = ['organame', 'firstname', 'lastname', 'address', 'zip', 'city', 'taxnumber'];

    constructor() {
        this._debugEnabled = window.location.href.indexOf('cloudcenter-testing') != -1;
        this._isSysAdmin = UrlHelpers.getMetaData('sysAdmin') === "1";
        this._previousSteps = new Array<OrderWizardPages>();

        this._isNewUser = UrlHelpers.getMetaData('isNewUser') === "1";
        this._isMultiOrgaUser = Array.apply(null, document.querySelectorAll('.wizardPage__existingOrganization--listitem')).length > 1;

        this._userDependentFirstPage();

        window.addEventListener("scroll", this._handleScrollShadow);

        this._enableSubscription();
        this._loadCookie();

        if (this._selectedTenantId) {
            console.info(`tenant id ${this._selectedTenantId} found. Goto app subscription summary`);
            this._currentStep = OrderWizardPages.AppSubscriptionSummary;
            if (this._currentTenantHasAppAlready()) {
                this._disableSubscription();
            }
        }

        this._addBtnPrevHandlers();
        this._addBtnNextHandlers();
        this._prepareTenantLists();
        this._addBtnDocumentMenu();
        const btnCreateTenant = <HTMLButtonElement>document.getElementById('btnCreateNewTenant');
        if (btnCreateTenant) {
            btnCreateTenant.addEventListener('click', this._handle_createNewTenant);
        }

        (<HTMLInputElement>document.getElementById('domain')).addEventListener('keyup', this._handle_domainvalidation);
        Array.apply(null, document.querySelectorAll('.app-info-button')).forEach((infoButton: HTMLButtonElement) => {
            infoButton.addEventListener('click', (e: Event) => {
                const modalTargetId = (<HTMLElement>e.target).dataset.targetModal;
                const modalTarget = <HTMLDivElement>document.getElementById(modalTargetId);
                const dialog = new mdc.dialog.MDCDialog(modalTarget);
                dialog.open();
            });
        });

        autoInit();

        this._initCreateOrganizationFields();

        if (!this._isNewUser) {
            const list = new mdc.list.MDCList(document.getElementById('existingOrganizationList'));
            list.singleSelection = true;
            list.listen("click", this._handle_selectOrganization);

            const tenantLists = document.querySelectorAll('.wizardPage__tenantSelection--list');

            Array.apply(null, tenantLists).forEach((tenantList: HTMLUListElement) => {
                const mdcList = new mdc.list.MDCList(tenantList);
                mdcList.singleSelection = true;
                mdcList.listen('click', this._handle_selectTenant);
            });

            this._addBtnCreateNewHandlers();
        }
        window.addEventListener('load', () => {
            this._navigateStep(this._currentStep);
        });
    }

    private _userDependentFirstPage = () => {
        if (this._isNewUser) {
            this._newUserCheckout();
            (<HTMLButtonElement>document.getElementById('btnChangeTenant')).addEventListener('click', () => {
                this._changeSelectionButtonHandler(OrderWizardPages.CreateTenant);
            });
            return;
        }
        if (this._isMultiOrgaUser) {
            this._multipleOrganisationUserCheckout();
            (<HTMLButtonElement>document.getElementById('btnChangeTenant')).addEventListener('click', () => {
                this._changeSelectionButtonHandler(OrderWizardPages.ExistingOrganization);
            });
            return;
        }
        this._singleOrganisationUser();
        (<HTMLButtonElement>document.getElementById('btnChangeTenant')).addEventListener('click', () => {
            this._changeSelectionButtonHandler(OrderWizardPages.ExistingTenant);
        });
    };

    private _newUserCheckout = () => {
        FieldHandler.setFieldValue('organame', this._i18n.getTranslation('default_organisation_name_my_systems'), true);
        this._currentStep = OrderWizardPages.CreateTenant;
    }

    private _multipleOrganisationUserCheckout = () => {
        this._currentStep = OrderWizardPages.ExistingOrganization;
    };

    private _singleOrganisationUser = () => {
        const orgaListItems = Array.apply(null, document.querySelectorAll('.wizardPage__existingOrganization--listitem'));
        this._selectedOrganizationId = orgaListItems[0].id;
        const tenantListItems = Array.apply(null, document.querySelectorAll('.wizardPage__tenantSelection--list-item'));
        //multi tenant checkout
        if (tenantListItems.length > 1) {
            this._currentStep = OrderWizardPages.ExistingTenant;
            this._showTenantListForOrga(this._selectedOrganizationId);
            return;
        }
        //no tenant checkout
        if (tenantListItems.length < 1) {
            this._currentStep = OrderWizardPages.CreateTenant;
            return;
        }
        //single tenant checkout
        const tenant = tenantListItems[0];
        this._tenantSelected(tenant.id, tenant.dataset.domain);
        this._currentStep = OrderWizardPages.AppSubscriptionSummary;
    };

    private _initCreateOrganizationFields = () => {
        this._createOrganizationInputIds.forEach((id) => {
            const element = document.getElementById(id);
            element.addEventListener("keyup", (e: KeyboardEvent) => {
                const target = <HTMLInputElement>e.currentTarget;
                this._validateMaterialInput(target.id);
                let allOrgaInputsValid = true;
                this._createOrganizationInputIds.forEach((id) => {
                    let currentElement = <HTMLDivElement>document.getElementById(`${id}_container`);
                    if (currentElement.classList.contains('mdc-text-field--invalid')) {
                        allOrgaInputsValid = false;
                    }
                });
                this._nextButtonDisable(!allOrgaInputsValid);
            })
        });
    };

    private _getPricingInformationWithRetries = (url: string, retries: number): Promise<string> => {
        console.log("Fetching for price information - remaining retries: ", retries - 1);
        const language = navigator.language == 'de' || navigator.language == 'de-DE' ? 'de' : 'en';
        let req = new Request(url, {
            method: 'GET',
            headers: {
                "accept": "text/html",
                "accept-language": language
            },
            credentials: 'same-origin',
        });
        return fetch(req).then(response => {
            if (!response.ok) {
                throw new Error(`get pricing information failed with status ${response.status}: ${response.statusText}`);
            }
            return response.text().then(html => {
                return html;
            }, rejected => {
                throw new Error(`cannot get response text: ${rejected}`);
            });
        }).catch(error => {
            if (retries < 2) {
                throw error
            }
            return this._getPricingInformationWithRetries(url, retries - 1);
        });
    };

    private _addBtnPrevHandlers = () => {
        let btnPrevious = document.querySelectorAll('.btnPagePrevious');
        Array.apply(null, btnPrevious).forEach((btn) => {
            btn.addEventListener('click', this._handle_prev);
        });
    };

    private _addBtnDocumentMenu = () => {
        let btnMenu = document.querySelectorAll('.wizardPage__summary-app-card__action');
        Array.apply(null, btnMenu).forEach((btn) => {
            if (!btn.hasAttribute('disabled')) {
                btn.addEventListener('click', this._handle_documentMenu);
            }
        });
    };

    private _addBtnNextHandlers = () => {
        (<HTMLButtonElement>document.getElementById(`${this._btnNextPrefix}createOrga`)).addEventListener('click', this._handle_createOrga_next);
        (<HTMLButtonElement>document.getElementById(`${this._btnNextPrefix}createTenant`)).addEventListener('click', this._handle_createTenant_next);
        (<HTMLButtonElement>document.getElementById(`${this._btnNextPrefix}appSubscriptionSummary`)).addEventListener('click', this._handle_appSubscriptionSummary_next);
        (<HTMLButtonElement>document.getElementById(`${this._btnNextPrefix}existingOrga`)).addEventListener('click', this._handle_existingOrga_next);
        (<HTMLButtonElement>document.getElementById(`${this._btnNextPrefix}existingTenant`)).addEventListener('click', this._handle_existingTenant_next);
    };

    private _addBtnCreateNewHandlers = () => {
        (<HTMLButtonElement>document.getElementById('btnCreateNewOrga')).addEventListener('click', this._handle_createNewOrganization);
    };

    private _handle_documentMenu = (e: Event): void => {
        const current = <HTMLButtonElement>e.currentTarget;
        const menuId = current.dataset.menu;
        const menuElement = document.getElementById(menuId);
        if (menuElement) {
            const menu = new mdc.menu.MDCMenu(menuElement);
            if (menu.open) {
                menu.open = false;
            } else {
                if (menu.items.length > 0) {
                    menu.open = true;
                }
            }
        }
    };

    private _handle_prev = (): void => {
        let prev = this._previousSteps.pop();
        if (prev === OrderWizardPages.AppSubscriptionSummary) {
            // if (!this._selectedTenantId) {
            //     if (this._currentStep != OrderWizardPages.ExistingTenant || (this._currentStep == OrderWizardPages.ExistingTenant && !this._selectedTenantDomain)) {
            //         const tenantIdFromCookie = this._getCookie('tenantId');
            //         const tenantElement = document.getElementById(tenantIdFromCookie);
            //         this._selectedTenantId = '';
            //         this._selectedTenantDomain = '';
            //         if (tenantElement && tenantIdFromCookie) {
            //             this._selectedTenantId = tenantIdFromCookie;
            //             this._selectedTenantDomain = tenantElement.dataset['domain'];
            //         }
            //     }
            // }
            // if (!this._selectedOrganizationId) {
            //     this._selectedOrganizationId = this._getCookie('organizationId');
            //     const orgaElement = document.getElementById(this._selectedOrganizationId);
            //     if (!orgaElement) {
            //         this._selectedOrganizationId = "";
            //     }
            // }
            if (this._currentTenantHasAppAlready()) {
                this._disableSubscription();
            }
        }
        if (this._currentStep == OrderWizardPages.CreateTenant && prev == OrderWizardPages.ExistingTenant) {
            this._undoDeselectTenant();
        }
        this._clear_user_interaction(this._currentStep, prev);
        this._currentStep = prev;
        this._navigateStep(prev);
    };

    private _clear_user_interaction = (currentstep: OrderWizardPages, prevStep: OrderWizardPages): void => {
        switch (currentstep) {
            case OrderWizardPages.CreateOrganization:
                if (prevStep === OrderWizardPages.ExistingOrganization) {
                    const textFieldIds = ['domain', 'organame', 'firstname', 'lastname', 'address', 'zip', 'city', 'taxnumber'];
                    textFieldIds.forEach((id) => {
                        MaterialHelpers.setTextFieldValue(`${id}_container`, '');
                    });
                    MaterialHelpers.removeTextFieldHelperText('domain_container');
                    this._nextButtonDisable(true, OrderWizardPages.CreateTenant);
                }
                break;
            case OrderWizardPages.CreateTenant:
                if (prevStep === OrderWizardPages.ExistingTenant) {
                    MaterialHelpers.setTextFieldValue('domain_container', '');
                    MaterialHelpers.removeTextFieldHelperText('domain_container');
                    this._nextButtonDisable(true, OrderWizardPages.CreateTenant);
                }
                break;
        }
    };

    private _handle_createOrga_next = (e: any): void => {
        if (this._areOrgaInputFieldsValid()) {
            const next = OrderWizardPages.CreateTenant;
            this._previousSteps.push(this._currentStep);
            this._currentStep = next;
            this._navigateStep(next);
        } else {
            console.error('Any organization fields are invalid. Please check!');
        }
    };

    private _handle_createTenant_next = (e: any): void => {
        this._hideBasisApp(false);
        this._enableSubscription();
        this._nextButtonDisable(false, OrderWizardPages.AppSubscriptionSummary);
        //mark: bugfix/clc-1113
        this._tenantSelected('', `https://${FieldHandler.getFieldValue('domain')}.d-velop.cloud`);
        const next = OrderWizardPages.AppSubscriptionSummary;
        this._previousSteps.push(this._currentStep);
        this._currentStep = next;
        this._navigateStep(next);
    };

    private _handle_appSubscriptionSummary_next = (e: any): void => {
        const next = OrderWizardPages.PrepairTenant;
        this._previousSteps.push(this._currentStep);
        this._currentStep = next;
        this._navigateStep(next);
        this._handle_subscribe();
        if (UrlHelpers.getMetaData("gtmActive") && !this._isSysAdmin) {
            this._googleTagManagerEvent();
        }
    };

    private _googleTagManagerEvent = () => {
        if (!UrlHelpers.getMetaData("gtmActive") || this._isSysAdmin) {
            return;
        }

        const transactionProducts = [];
        let apps = Array.apply(null, document.querySelectorAll('.dvApp'));

        for (const el of apps) {
            if (!el.dataset.approved) {
                return;
            }
            transactionProducts.push({
                'sku': el.dataset['appname'],
                'name': el.dataset['displayname'],
                'category': 'Allgemein',
                'price': 0.00,
                'quantity': 1
            });
        }

        if (transactionProducts.length < 1) {
            return;
        }

        let affiliate = UrlHelpers.getQueryParameter(window.location.href, 'affiliate') || 'cloudcenter';

        const transactionId = `${this._selectedTenantId}-${this._getTimestamp()}`;

        // @ts-ignore
        window.dataLayer = window.dataLayer || [];
        const event = {
            'event': 'orderCompleted',
            'transactionId': transactionId,      // Transaction ID. Required  for purchases and refunds. //BestellID / Rechnungsnummer (bis dahin tenant id + timestamp)
            'transactionTotal': 0.00,    // Total transaction value (incl. tax and shipping)
            'transactionTax': 0.00,
            'transactionShipping': 0,
            'transactionAffiliation': affiliate,
            'transactionProducts': transactionProducts
        };
        // @ts-ignore
        window.dataLayer.push(event);
    };

    private _googlePageView = (target: OrderWizardPages) => {
        //mark: deactive to prevent colliding page view funnels from new wizard
        return;
        // if (!UrlHelpers.getMetaData("gtmActive") || this._isSysAdmin) {
        //     return;
        // }
        //
        // const mapping = this._getGoogleAnalyticsKey(target);
        // console.log(`setting page for "${target}" as "${mapping.pagePath}"`);
        // // @ts-ignore
        // window.dataLayer = window.dataLayer || [];
        // // @ts-ignore
        // window.dataLayer.push({
        //     'event': 'Pageview',
        //     'pagePath': mapping.pagePath,
        //     'pageTitle': mapping.pageTitle
        // });
    };

    private _getGoogleAnalyticsKey = (pageKey: OrderWizardPages): GoogleTagManagerMapping => {
        let keyMap: { [key: string]: GoogleTagManagerMapping } = {};
        keyMap[OrderWizardPages.ExistingOrganization] = {pagePath: '/center/subscription/selectOrganization', pageTitle: 'Select Organization'};
        keyMap[OrderWizardPages.CreateOrganization] = {pagePath: '/center/subscription/newOrganization', pageTitle: 'New Organization'};
        keyMap[OrderWizardPages.ExistingTenant] = {pagePath: '/center/subscription/selectTenant', pageTitle: 'Select Tenant'};
        keyMap[OrderWizardPages.CreateTenant] = {pagePath: '/center/subscription/newTenant', pageTitle: 'New Tenant'};
        keyMap[OrderWizardPages.AppSubscriptionSummary] = {pagePath: '/center/subscription/summary', pageTitle: 'Summary'};
        keyMap[OrderWizardPages.PrepairTenant] = {pagePath: '/center/subscription/finish', pageTitle: 'Finish'};
        return keyMap[pageKey] || {pagePath: '/', pageTitle: 'unknown'};
    };

    private _getTimestamp = (): string => {
        const d = new Date();
        const year = d.getFullYear().toString();
        const month = d.getMonth() + 1 < 10 ? "0" + (d.getMonth() + 1).toString() : (d.getMonth() + 1).toString();
        const day = d.getDate() < 10 ? "0" + d.getDate().toString() : d.getDate().toString();
        const hours = d.getHours() < 10 ? "0" + d.getHours().toString() : d.getHours().toString();
        const minutes = d.getMinutes() < 10 ? "0" + d.getMinutes().toString() : d.getMinutes().toString();
        const seconds = d.getSeconds() < 10 ? "0" + d.getSeconds().toString() : d.getSeconds().toString();
        const milliseconds = d.getMilliseconds() < 10 ? "0" + d.getMilliseconds().toString() : d.getMilliseconds().toString();
        return (year + month + day + hours + minutes + seconds + milliseconds.slice(0, 2));
    };

    private _navigateStep = (targetId: OrderWizardPages) => {
        const pages = Array.apply(null, document.querySelectorAll('.wizardPage'));
        pages.forEach((page) => {
            if (page.id === targetId) {
                page.classList.add('current');
            } else {
                page.classList.remove('current');
            }
        });

        const header = document.getElementById('orderWizardMainHeader');
        if (targetId === OrderWizardPages.AppSubscriptionSummary) {
            header.innerText = this._i18n.getTranslation('order_wizard_section_subscriptionSummary_title');
            document.getElementById('appSubscriptionSummary_tenant_domain').innerText = this._selectedTenantDomain;
            if (this._selectedTenantId) {
                const tenantElement = document.getElementById(this._selectedTenantId);
                if (tenantElement && tenantElement.hasAttribute('aria-disabled')) {
                    this._nextButtonDisable(true, OrderWizardPages.AppSubscriptionSummary);
                }
            }
        } else {
            header.innerText = this._i18n.getTranslation('order_wizard_header_text');
        }

        if (this._previousSteps.length < 1) {
            this._hidePreviousButton(targetId);
        } else {
            this._hidePreviousButton(targetId, false);
        }

        this._handleScrollShadow();
        this._googlePageView(targetId)
    };

    private _areOrgaInputFieldsValid = (): boolean => {
        // const inputs = ['organame', 'firstname', 'lastname', 'address', 'zip', 'city', 'taxnumber'];
        const inputs = this._createOrganizationInputIds;
        let allValid = true;
        inputs.forEach((id: string) => {
            if (!this._validateMaterialInput(id)) {
                allValid = false;
            }
        });
        if (allValid) {
            this._nextButtonDisable(false)
        } else {
            this._nextButtonDisable();
        }
        return allValid;
    };

    private _validateMaterialInput = (inputId: string): boolean => {
        let inputValue = (<HTMLInputElement>document.getElementById(inputId)).value;
        // const helperTextId = `${inputId}HelperText`;
        const containerId = `${inputId}_container`;
        if (inputValue.isNullOrEmpty()) {
            MaterialHelpers.setTextFieldHelperText(containerId, this._i18n.getTranslation('order_wizard_error_field_empty_message'), true);
            return false;
        }
        if (!isOnlyText(inputValue)) {
            MaterialHelpers.setTextFieldHelperText(containerId, this._i18n.getTranslation('order_wizard_error_field_invalid_char_message'), true);
            return false;
        }
        MaterialHelpers.removeTextFieldHelperText(containerId);
        return true;
    };

    private _handle_domainvalidation = () => {
        if (!this._isDomainCheckRunning) {
            this._nextButtonDisable();
            let spin: string = `<div><i class="material-icons orderWizard_spin" style="vertical-align: middle;">autorenew</i>&nbsp;${this._i18n.getTranslation('tenant_checkdomain_btn_save_waittitle')}</span></div>`;
            MaterialHelpers.setTextFieldHelperTextWithHtmlContent('domain_container', 'domainHelperText', spin, false);
            this._isDomainCheckRunning = true;

            let domain = FieldHandler.getFieldValue('domain');
            setTimeout(() => {
                this._domainUpdatedCheck(domain);
            }, this._domainCheckTimerMS);
        }
    };

    private _domainUpdatedCheck = (domain: string) => {
        let domainUpdated = FieldHandler.getFieldValue('domain');
        if (domain !== domainUpdated) {
            setTimeout(() => {
                this._domainUpdatedCheck(domainUpdated);
            }, this._domainCheckTimerMS);
        } else {
            this._isDomainValid(domainUpdated);
        }
    };

    private _isDomainValid = (domain: string) => {
        // regex check
        if (!isDomainValid(domain)) {
            console.error('domain field contains invalid characters for domain');
            return;
        }

        let url = UrlHelpers.getLinkRelHref('domainCheck');
        let req = new Request(url, {
            method: 'post',
            headers: {
                'accept': 'application/json'
            },
            body: JSON.stringify({
                domain: domain,
                tenantId: ''
            }),
        });
        fetch(req, {credentials: "same-origin"})
            .then((response) => {
                if (response.status === 200) {
                    let result = response.json();
                    result.then(value => {
                        if (value.exists) {
                            let domainUpdated = FieldHandler.getFieldValue('domain');
                            if (domainUpdated === domain) {
                                let message: string;
                                if (value.message === "Domain already exists.") {
                                    message = this._i18n.getTranslation('tenant_subdomain_error_domain_already_exist');
                                } else if (value.message === "Domain is blacklisted.") {
                                    message = this._i18n.getTranslation('tenant_subdomain_error_domain_is_blacklisted');
                                } else if (value.message === "Domain contains invalid characters.") {
                                    message = this._i18n.getTranslation('tenant_subdomain_error_invalidchar');
                                } else {
                                    message = this._i18n.getTranslation('tenant_subdomain_error_domain_already_exist');
                                }
                                console.error(value.message);
                                MaterialHelpers.setTextFieldHelperText('domain_container', message, true);
                                this._nextButtonDisable();
                                this._isDomainCheckRunning = false;
                            } else {
                                this._isDomainValid(domainUpdated);
                            }
                        } else {
                            let domainUpdated = FieldHandler.getFieldValue('domain');
                            if (domainUpdated === domain) {
                                let msg = `<div><i class="material-icons" style="color: white;background-color: green;border-radius: 50%;vertical-align: middle;">done</i>&nbsp;${this._i18n.getTranslation('tenant_subdomain_successs_message')}</div>`;
                                MaterialHelpers.setTextFieldHelperTextWithHtmlContent('domain_container', 'domainHelperText', msg, false);
                                this._nextButtonDisable(false);
                                this._isDomainCheckRunning = false;
                            } else {
                                this._isDomainValid(domainUpdated);
                            }
                        }
                    })
                } else {
                    let domainUpdated = FieldHandler.getFieldValue('domain');
                    if (domainUpdated === domain) {
                        MaterialHelpers.setTextFieldHelperText('domain_container', this._i18n.getTranslation('tenant_subdomain_error_unexpected_error'), true);
                        this._isDomainCheckRunning = false;
                        this._nextButtonDisable();
                    } else {
                        this._isDomainValid(domainUpdated);
                    }
                }
            })
            .catch(error => {
                let domainUpdated = FieldHandler.getFieldValue('domain');
                if (domainUpdated === domain) {
                    MaterialHelpers.setTextFieldHelperText('domain_container', this._i18n.getTranslation('tenant_subdomain_error_unexpected_error'), true);
                    console.error(error);
                    this._nextButtonDisable();
                } else {
                    this._isDomainValid(domainUpdated);
                }
            });
    };

    private _nextButtonDisable = (disable: boolean = true, step: OrderWizardPages = OrderWizardPages.Current) => {
        let btnNextId = `${this._btnNextPrefix}${this._currentStep}`;
        if (step !== OrderWizardPages.Current) {
            btnNextId = `${this._btnNextPrefix}${step}`;
        }
        let btnNext = document.getElementById(btnNextId);
        if (disable) {
            btnNext.setAttribute('disabled', 'disabled');
        } else {
            btnNext.removeAttribute('disabled');
        }
    };

    private _handle_subscribe = () => {
        this.logDebug("_handle_subscribe called");
        this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_begin'));
        this._createOrga().then((orgaId: string) => {
            this._createTenant(orgaId).then((tenantId: string) => {
                this._subscribeApps(tenantId).then(() => {
                    this.logDebug(`_handle_subscribe - orgaID: ${orgaId}, tenantID: ${tenantId}`);
                    this._setCookie(this._selectedOrganizationId, this._selectedTenantId);
                    this._subscribeSuccess(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_success'));
                }).catch((err) => {
                    if (err) {
                        console.error(err);
                        this._subscribeError(this._i18n.getTranslation('order_wizard_error_appCouldNotBeSubscribed'));
                    }
                    return;
                });
            }).catch((err) => {
                console.error(err);
                this._subscribeError(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_new_tenant_error'));
                return;
            });
        }).catch(() => {
            this.logDebug("_handle_subscribe - createOrga catch block");
            this._subscribeError(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_new_organization_error'));
            return;
        });
    };

    private _createOrga = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            if (this._selectedOrganizationId) {
                resolve(this._selectedOrganizationId);
                return;
            }
            this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_new_organization'));
            let url = UrlHelpers.getLinkRelHref('organizations');
            if (url.isNullOrEmpty()) {
                console.error('Cannot find link relation for "organizations"');
            }
            let orgaName = FieldHandler.getFieldValue('organame');
            if (!isOnlyText(orgaName)) {
                reject('organization name invalid');
                return;
            }
            let req = new Request(url, {
                method: 'POST',
                headers: {
                    'accept': 'application/json'
                },
                body: JSON.stringify({
                    name: orgaName
                }),
            });
            fetch(req, {credentials: "same-origin"})
                .then((response) => {
                    if (response.status === 201) {
                        let orgaUrl = response.headers.get('location');
                        response.json().then(value => {
                            let orgaId = value.replace(/"/g, '');
                            this._selectedOrganizationId = orgaId;
                            console.log(`create organization success. ID: ${orgaId}`);
                            this._updateOrga(orgaId, orgaName, orgaUrl)
                                .then(() => {
                                    resolve(orgaId);
                                    return;
                                })
                                .catch((error) => {
                                    reject(error);
                                    return;
                                });
                        });
                    } else {
                        response.json().then((value: errorDto) => {
                            //todo: läuft hier in fehler "Organization name missing"
                            console.error(`create organization failed with status ${response.status}: ${value.description}`);
                            reject(value.description);
                            return;
                        });
                    }
                })
                .catch((error) => {
                    error.json().then((value: errorDto) => {
                        console.error(`create organization failed with status ${error.status}: ${value.description}`);
                        reject(value.description);
                        return;
                    });
                    reject(error);
                });
        });
    };

    private _updateOrga = (orgaId: string, orgaName: string, orgaUrl: string): Promise<string> => {
        let promise = new Promise<string>((resolve, reject) => {
            let address = FieldHandler.getFieldValue('address');
            let zip = FieldHandler.getFieldValue('zip');
            let city = FieldHandler.getFieldValue('city');
            let firstName = FieldHandler.getFieldValue('firstname');
            let lastName = FieldHandler.getFieldValue('lastname');
            let taxNumber = FieldHandler.getFieldValue('taxnumber');
            let req = new Request(orgaUrl, {
                method: 'PUT',
                headers: {
                    'accept': 'application/json'
                },
                body: JSON.stringify({
                    organization: {
                        id: orgaId,
                        name: orgaName,
                        street: address,
                        zip: zip,
                        city: city,
                        contactFirstName: firstName,
                        contactLastName: lastName,
                        taxNumber: taxNumber
                    }
                })
            });
            fetch(req, {credentials: "same-origin"})
                .then((response) => {
                    if (response.status === 200) {
                        console.log(`update organization ${orgaId} success`);
                        resolve(orgaId);
                    } else {
                        response.json().then((value: errorDto) => {
                            console.error(`update organization information failed with status ${response.status}: ${value.description}`);
                            reject(value.description);
                            return
                        });
                        reject(response.statusText);
                    }
                })
                .catch((error) => {
                    error.json().then((value: errorDto) => {
                        console.error(`create organization failed with status ${error.status}: ${value.description}`);
                        reject(value.description);
                        return
                    });
                    reject(error);
                });
        });
        return promise;
    };

    private _createTenant = (orgaId: string): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            if (this._selectedTenantId) {
                resolve(this._selectedTenantId);
                return;
            }
            this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_new_tenant'));
            let url = `${UrlHelpers.getLinkRelHref('organizations')}/${orgaId}`;
            let req = new Request(url, {
                method: 'POST',
                headers: {
                    'accept': 'application/json'
                },
            });
            fetch(req, {credentials: "same-origin"})
                .then((response) => {
                    if (response.status != 201) {
                        response.json().then((value: errorDto) => {
                            reject(value.description);
                            return;
                        });
                        return;
                    }
                    let tenantUrl = response.headers.get('location');
                    response.json().then(value => {
                        console.info(value);
                        let tenantId = value.replace(/"/g, '');
                        this._selectedTenantId = tenantId;
                        this._updateTenant(orgaId, tenantId, tenantUrl).then((tenantId: string) => {
                            resolve(tenantId);
                            return;
                        }).catch((error) => {
                            reject(error);
                            return;
                        });
                    });
                })
                .catch((error) => {
                    error.json().then((value: errorDto) => {
                        console.error(`create organization failed with status ${error.status}: ${value.description}`);
                        reject(value.description);
                        return;
                    });
                    reject(error);
                });
        })
    };

    private _updateTenant = (orgaId: string, tenantId: string, tenantUrl: string): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            let domain = FieldHandler.getFieldValue('domain');
            let req = new Request(tenantUrl, {
                method: 'PUT',
                headers: {
                    'accept': 'application/json'
                },
                body: JSON.stringify({
                    tenant: {
                        id: tenantId,
                        organizationId: orgaId,
                        domainName: domain,
                    }
                })
            });
            fetch(req, {credentials: "same-origin"})
                .then((response) => {
                    if (!response.ok) {
                        response.json().then((value: errorDto) => {
                            reject(value.description);
                            return;
                        });
                        return;
                    }
                    resolve(tenantId);
                    return;
                })
                .catch((error) => {
                    error.json().then((value: errorDto) => {
                        console.error(`create organization failed with status ${error.status}: ${value.description}`);
                        reject(value.description);
                        return;
                    });
                    reject(error);
                    return;
                });
        });
    };

    private _subscribeApps = (tenantId: string): Promise<null> => {
        this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_subscribing_app'));
        return new Promise((resolve, reject) => {
            let apps = Array.apply(null, document.querySelectorAll('.dvApp'));
            for (let i = 0; i < apps.length; i++) {
                let app = <HTMLElement>apps[i];
                let appName = app.dataset['appname'];
                if (appName !== 'basis') {
                    this._subscribeApp(tenantId, appName)
                        .then(() => {
                            resolve();
                        })
                        .catch((err) => {
                            reject(err);
                        });
                }
            }
        });
    };

    private _subscribeApp = (tenantId: string, appName: string): Promise<boolean> => {
        let promise = new Promise<boolean>(((resolve, reject) => {
            let url = UrlHelpers.getLinkRelHref('subscription');
            let req = new Request(url, {
                method: 'PUT',
                headers: {
                    'accept': 'application/json',
                    // @ts-ignore
                    'accept-language': navigator.language || navigator.userLanguage
                },
                body: JSON.stringify({
                    action: "subscribe",
                    tenantId: tenantId,
                    appName: appName
                })
            });
            fetch(req, {credentials: "same-origin"})
                .then((response) => {
                    if (response.ok) {
                        console.log(`subscribe app ${appName} success`);
                        resolve(true);
                    } else if (response.status === 400) {
                        response.json().then((dto) => {
                            let errorDto: errorDto = dto;
                            if (errorDto) {
                                switch (errorDto.description.toLowerCase()) {
                                    case "app to subscribe is on premise app":
                                        console.error(`app ${appName} cannot subscribe in tenant ${tenantId}. Maybe tenant use d.velop platform connect and the app to subscribe is running locally.`);
                                        this._subscribeError(this._i18n.getTranslation('order_wizard_error_appToSubscribeIsOnPremiseApp_description'));
                                        reject();
                                        return;
                                    case "one or more app dependencies are on premise apps":
                                        console.warn(`app ${appName} has dependencies who are running over by d.velop platform connect`);
                                        this._subscribeSuccess(this._i18n.getTranslation('order_wizard_error_appToSubscribeHasOnPremiseRunningDependencies_description'));
                                        resolve(true);
                                        return;
                                    default:
                                        console.error(`subscribe app ${appName} failed with status ${response.status}: ${errorDto.description}`);
                                        reject(errorDto.description);
                                        return;
                                }
                            }
                            console.error(`cannot parse error dto: `, dto);
                            reject(dto)
                        });
                    } else {
                        console.error(`subscribe app ${appName} failed with status ${response.status}: ${response.statusText}`);
                        reject(response.statusText);
                    }
                })
                .catch((error) => {
                    error.json().then((value: errorDto) => {
                        console.error(`create organization failed with status ${error.status}: ${value.description}`);
                        reject(value.description);
                        return
                    });
                    reject(error);
                });
        }));
        return promise;
    };

    private _getGettingStartedRelativeLink = (): string => {
        const appCards = document.querySelectorAll(".wizardPage__summary-app-card");
        const gettingStartedLinks: { [app: string]: string; } = {};
        Array.apply(null, appCards).forEach((card: HTMLDivElement) => {
            if (card.dataset.gettingstarted && card.dataset.appname) {
                gettingStartedLinks[card.dataset.appname.toLowerCase()] = card.dataset.gettingstarted;
            }
        });

        const keys = Object.keys(gettingStartedLinks);
        if (keys.length < 1) {
            console.log("[Information] no getting started link found");
            return "";
        }

        const dvDocuments = "dvelop-documents";
        if (gettingStartedLinks[dvDocuments]) {
            console.log("[Information] found link for", dvDocuments);
            return gettingStartedLinks[dvDocuments];
        }

        keys.sort();

        return gettingStartedLinks[keys[0]];
    };

    private _prepareTenantLists = () => {
        const listItems = document.querySelectorAll('.wizardPage__tenantSelection--list-item');
        Array.apply(null, listItems).forEach((li: HTMLLIElement) => {
            this._setIconColorForListItem(li);
        });
    };

    private _showTenantListForOrga = (orgaId: string) => {
        const lists = document.querySelectorAll('.wizardPage__tenantSelection--list');
        Array.apply(null, lists).forEach((list: HTMLUListElement) => {
            list.classList.remove('show');
            if (list.id === 'tenantSelectList_' + orgaId) {
                list.classList.add('show');
            }
        });
    };

    private _setIconColorForListItem = (listItem: HTMLLIElement) => {
        const tenantName = listItem.getElementsByClassName('wizardPage__tenantSelection--list-item__primary-text')[0].textContent;
        const icon = <HTMLSpanElement>listItem.getElementsByClassName('wizardPage__tenantSelection--list-icon')[0];
        const saturation = 70;
        const lightness = 70;
        icon.style.backgroundColor = `hsl(${this._getHueByString(tenantName)} ${saturation}% ${lightness}%)`;
    };

    private _getHueByString = (text: string): number => {
        var charCodeSum = 0;
        for (var i = 0; i < text.length; i++) {
            charCodeSum += text.charCodeAt(i);
        }
        return charCodeSum % 255;
    };

    private _setSelectedTenantId(value: string) {
        const nextButton = <HTMLButtonElement>document.getElementById('btnNext_existingTenant');
        nextButton.setAttribute('disabled', 'disabled');
        if (value || value !== "") {
            nextButton.removeAttribute('disabled');
        }
        this._selectedTenantId = value;
        if (this._currentTenantHasAppAlready()) {
            this._disableSubscription();
        } else {
            this._enableSubscription();
        }
    }

    private _subscribeStartAnimation = (message: string) => {
        if (!this._animationActive) {
            this._startAnimation();
            this._hideElement('btnGetStarted');
            this._hideElement('btnPrepairTenantFailed');
        }
        this._setSubscribeAnimationMessage(message);
    };

    private _subscribeSuccess = (message: string) => {
        this.logDebug("_subscribeSuccess called");
        const header = document.getElementById('orderWizardMainHeader');
        header.innerText = this._i18n.getTranslation('order_wizard_header_text');
        const baseTenantUrl = this._selectedTenantDomain ? this._selectedTenantDomain : `https://${FieldHandler.getFieldValue('domain')}.d-velop.cloud`;
        const relativeLink = this._getGettingStartedRelativeLink();
        const tenantUrl = baseTenantUrl + relativeLink;

        setTimeout(() => {
            this._setGettingStartedButtonHandler(tenantUrl);
            this._setSubscribeAnimationMessage(message);
            this._showElement('btnGetStarted');
            this._hideElement('btnPrepairTenantFailed');
            this._animationSuccess();
        }, 5000);
    };

    private _setGettingStartedButtonHandler(url: string) {
        (<HTMLButtonElement>document.getElementById('btnGetStarted')).addEventListener('click', () => {
            open(url, '_blank')
        });
    }

    private _checkTargetAvailability = (url: string, retries: number): Promise<boolean> => {
        console.log("Checking target availability - remaining retries: ", retries - 1);
        let req = new Request(url, {
            method: 'GET',
            mode: "no-cors",
            headers: {
                "accept": "text/html",
            },
        });

        const waitFetch = new Promise(resolve => {
            setTimeout(() => {
                resolve()
            }, 2000);
        });

        return waitFetch.then(() => {
            return fetch(req).then(response => {
                this.logDebug("_checkTargetAvailability - fetch response code " + response.status);
                if (response.status < 400) {
                    this.logDebug("_checkTargetAvailability - positive response");
                    return true;
                }
                throw new Error(`unexpected response from tenant - status ${response.status}`)
            }).catch(error => {
                console.warn(`got error checking ${url} - error: ${error.toString()}`);
                if (retries < 2) {
                    return false
                }
                return this._checkTargetAvailability(url, retries - 1);
            });
        })
    };

    private _subscribeError = (message: string) => {
        const header = document.getElementById('orderWizardMainHeader');
        header.innerText = this._i18n.getTranslation('order_wizard_error_header_for_summary_title');
        this._setSubscribeAnimationMessage(message);
        this._showElement('btnPrepairTenantFailed');
        this._hideElement('btnGetStarted');
        this._animationError();
    };

    private _startAnimation = () => {
        const src = 'https://s3.eu-central-1.amazonaws.com/mediamanager.cloudcenter.service.d-velop.cloud/2c5098e9-52ee-44c2-ba65-19f19d9fe535';
        this._setAnimationImageSource(src, true);
        this._animationActive = true;
    };

    private _animationSuccess = () => {
        const src = 'https://s3.eu-central-1.amazonaws.com/mediamanager.cloudcenter.service.d-velop.cloud/9e332407-d3cf-442f-a01a-9037976519ab';
        this._setAnimationImageSource(src, false);
        this._animationActive = false;
    };

    private _animationError = () => {
        const src = 'https://s3.eu-central-1.amazonaws.com/mediamanager.cloudcenter.service.d-velop.cloud/bd28bb14-20e4-47ae-8a7d-688b56ed5eda';
        this._setAnimationImageSource(src, false);
        this._animationActive = false;
    };

    private _setAnimationImageSource = (src: string, spin: boolean) => {
        const image = <HTMLImageElement>document.getElementById('loading-animation-icon');
        image.src = src;
        spin ? image.classList.add('orderWizard_spin') : image.classList.remove('orderWizard_spin');
    };

    private _setSubscribeAnimationMessage = (message: string) => {
        const h = <HTMLHeadingElement>document.getElementById('orderWizard-animation-message');
        h.innerHTML = message;
    };

    private _hideElement = (elementId: string) => {
        (<HTMLElement>document.getElementById(elementId)).style.display = 'none';
    };

    private _showElement = (elementId: string) => {
        (<HTMLElement>document.getElementById(elementId)).style.display = 'block';
    };

    private _handle_selectOrganization = (e: any) => {
        const orgaList = document.getElementById('existingOrganizationList');
        let selectedElement = orgaList.querySelector('.mdc-list-item--selected');
        const elementId = selectedElement.id;
        const elementDataSet = selectedElement.getAttribute('data-isAdmin');
        this._setBtnCreateTenantDisabled(!(elementDataSet === "true"));
        this._selectedOrganizationId = elementId;
        this._showTenantListForOrga(elementId);
        this._deselectTenant();
        // check if user is allow to administrate orga by getting selected organization from store
        this._nextButtonDisable(false);
    };

    private _handle_selectTenant = (e: Event) => {
        const li = this._getListItemParent(<HTMLElement>e.target);
        if (li.classList.contains('mdc-list-item--disabled')) {
            this._nextButtonDisable();
            li.classList.remove('mdc-list-item--selected');
            this._selectedTenantListItem = null;
            return;
        }
        this._nextButtonDisable(false);
        this._selectedTenantListItem = li;
    };

    private _getListItemParent = (htmlElement: HTMLElement): HTMLLIElement => {
        if (htmlElement.tagName == 'LI') {
            return <HTMLLIElement>htmlElement;
        }
        return this._getListItemParent(htmlElement.parentElement);
    };

    private _tenantSelected = (id: string, domain: string) => {
        this._setSelectedTenantId(id);
        this._selectedTenantDomain = domain;
    };

    private _deselectTenant = () => {
        this._deselectedTenant = {id: this._selectedTenantId, domain: this._selectedTenantDomain};
        const listItems = document.querySelectorAll('.wizardPage__tenantSelection--list-item');
        Array.apply(null, listItems).forEach((li: HTMLLIElement) => {
            li.classList.remove('mdc-list-item--selected');
        });
        this._tenantSelected('', '');
    };

    private _undoDeselectTenant = () => {
        if (!this._deselectedTenant) {
            console.log('deselectedTenant not found - returning function');
            return;
        }
        console.log('deselected tenant ', this._deselectedTenant);
        const listItems = document.querySelectorAll('.wizardPage__tenantSelection--list-item');
        if (this._deselectedTenant && this._deselectedTenant.id) {
            Array.apply(null, listItems).forEach((li: HTMLLIElement) => {
                if (li.id == this._deselectedTenant.id) {
                    li.classList.add('mdc-list-item--selected');
                }
            });
        }
        this._tenantSelected(this._deselectedTenant.id, this._deselectedTenant.domain);
        this._deselectedTenant = null;
    }

    private _handle_createNewTenant = () => {
        this._deselectTenant();
        this._previousSteps.push(this._currentStep);
        const next = OrderWizardPages.CreateTenant;
        this._navigateStep(next);
        this._currentStep = next;
    };

    private _handle_createNewOrganization = () => {
        this._unselectItem_OrderList();
        this._selectedTenantId = '';
        this._selectedTenantDomain = '';

        this._nextButtonDisable();
        const next = OrderWizardPages.CreateOrganization;
        this._navigateStep(next);
        this._previousSteps.push(this._currentStep);
        this._currentStep = next;
        this._nextButtonDisable();
    };

    private _unselectItem_OrderList = () => {
        this._selectedOrganizationId = "";
        let orderList = document.getElementById('existingOrganizationList');
        let selectedItem = orderList.querySelector('.mdc-list-item--selected');
        if (selectedItem) {
            selectedItem.classList.remove('mdc-list-item--selected');
        }
    };

    private _handle_existingOrga_next = (e: any): void => {
        const next = OrderWizardPages.ExistingTenant;
        this._previousSteps.push(this._currentStep);
        this._currentStep = next;
        this._navigateStep(next);
    };

    private _handle_existingTenant_next = () => {
        this._hideBasisApp();
        if (!this._areAnyAppsDisplayed()) {
            this._nextButtonDisable(true, OrderWizardPages.AppSubscriptionSummary);
        }
        if (!this._selectedTenantListItem) {
            console.log("cannot set selected tenant - no list item found");
            return;
        }
        this._tenantSelected(this._selectedTenantListItem.id, this._selectedTenantListItem.dataset.domain);
        const next = OrderWizardPages.AppSubscriptionSummary;
        this._previousSteps.push(this._currentStep);
        this._currentStep = next;
        this._navigateStep(next);
    };

    private _hideBasisApp = (hide: boolean = true) => {
        let apps = document.getElementsByClassName('dvApp');
        let basisApp: HTMLDivElement;
        for (let i = 0; i < apps.length; i++) {
            let app = <HTMLDivElement>apps[i];
            if (app.dataset.appname === 'basis') {
                basisApp = app;
                break;
            }
        }
        if (basisApp) {
            if (hide) {
                basisApp.classList.add('wizardPage__element_hide');
            } else {
                basisApp.classList.remove('wizardPage__element_hide');
            }
        } else {
            console.log('basis app not found!');
        }
    };

    private _areAnyAppsDisplayed = (): boolean => {
        let apps = document.getElementsByClassName('dvApp');

        for (let i = 0; i < apps.length; i++) {
            let app = <HTMLDivElement>apps[i];
            if (!app.classList.contains('wizardPage__element_hide')) {
                return true;
            }
        }
        return false;
    };

    private _showLegalNotice = () => {
        const legalParagraph = <HTMLParagraphElement>document.getElementById("appSubscriptionSummaryLegalNotice");
        const alreadySubscribedParagraph = <HTMLParagraphElement>document.getElementById("appSubscriptionSummaryAlreadySubscribedNotice");
        if (legalParagraph && alreadySubscribedParagraph) {
            legalParagraph.classList.remove("wizardPage__element_hide");
            alreadySubscribedParagraph.classList.add("wizardPage__element_hide");
        }
    };

    private _showAlreadySubscribedNotice = () => {
        const legalParagraph = <HTMLParagraphElement>document.getElementById("appSubscriptionSummaryLegalNotice");
        const alreadySubscribedParagraph = <HTMLParagraphElement>document.getElementById("appSubscriptionSummaryAlreadySubscribedNotice");
        if (legalParagraph && alreadySubscribedParagraph) {
            legalParagraph.classList.add("wizardPage__element_hide");
            alreadySubscribedParagraph.classList.remove("wizardPage__element_hide");
        }
    };

    private _currentTenantHasAppAlready = (): boolean => {
        if (this._selectedTenantId == "") {
            return false
        }
        var tenantsFromMeta = UrlHelpers.getMetaData('tenants');
        if (!tenantsFromMeta) {
            return false;
        }
        let orgaTenants = JSON.parse(tenantsFromMeta);
        const tenants = orgaTenants[this._selectedOrganizationId] as Array<any>;
        if (!tenants) {
            return false;
        }
        let r = false;
        tenants.forEach((t) => {
            if (t.id == this._selectedTenantId) {
                r = t.appIsSubscribed;
            }
        });
        return r;
    };

    private _disableSubscription = () => {
        const b = <HTMLButtonElement>document.getElementById("btnNext_appSubscriptionSummary");
        if (b) {
            b.disabled = true;
            this._showAlreadySubscribedNotice();
        }
    };

    private _enableSubscription = () => {
        const b = <HTMLButtonElement>document.getElementById("btnNext_appSubscriptionSummary");
        if (b) {
            b.disabled = false;
            this._showLegalNotice();
        }
    };

    private _setBtnCreateTenantDisabled(disable: boolean) {
        let btn = <HTMLButtonElement>document.getElementById('btnCreateNewTenant');
        if (btn.dataset.hitLimit === "true") {
            disable = true;
        }
        btn.disabled = disable;
    }

    private _loadCookie = () => {
        if (this._selectedOrganizationId) {
            return;
        }
        this._selectedOrganizationId = this._getCookie('organizationId');
        if (this._selectedOrganizationId === "") {
            this._selectedOrganizationId = "";
            this._selectedTenantId = "";
            this._selectedTenantDomain = "";
            return
        }
        let orgaElement = document.getElementById(this._selectedOrganizationId);
        if (!orgaElement) {
            console.log(`organization ${this._selectedOrganizationId} not found. Clear all selected properties`);
            this._selectedOrganizationId = "";
            this._selectedTenantId = "";
            this._selectedTenantDomain = "";
            return;
        }
        if (orgaElement.dataset['isadmin'] !== 'true') {
            console.log(`current user has no admin permission in organization ${this._selectedOrganizationId}. Clear all selected properties`);
            this._selectedOrganizationId = "";
            this._selectedTenantId = "";
            this._selectedTenantDomain = "";
            return
        }
        this._setSelectedTenantId(this._getCookie('tenantId'));
        let tenantElement = document.getElementById(this._selectedTenantId);
        if (!tenantElement) {
            console.log(`tenant ${this._selectedTenantId} not found. Clear all selected properties`);
            this._selectedOrganizationId = "";
            this._selectedTenantId = "";
            this._selectedTenantDomain = "";
            return;
        }

        this._selectedTenantDomain = tenantElement.dataset['domain'];
        this._hideBasisApp();
        this._hidePreviousButton(OrderWizardPages.AppSubscriptionSummary)
    };

    private _setCookie = (organizationId: string, tenantId: string) => {
        let expireDate = new Date();
        expireDate.setTime(expireDate.getTime() + (365 * 24 * 60 * 60 * 1000));
        let expires = "expires=" + expireDate.toUTCString();
        let path = `path=${location.pathname}`;

        if (this._cookieExists('organizationId')) {
            this._deleteCookie('organizationId');
        }
        document.cookie = `organizationId=${organizationId};${expires};${path}`;

        if (this._cookieExists('tenantId')) {
            this._deleteCookie('tenantId');
        }
        document.cookie = `tenantId=${tenantId};${expires};${path}`;
    };

    private _deleteCookie = (name: string) => {
        let path = location.pathname;
        document.cookie = `${name};expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path}`;
    };

    private _getCookie = (name: string): string => {
        name = name + "=";
        let decodedCookie = decodeURIComponent(document.cookie);
        let cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) == ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(name) == 0) {
                return cookie.substring(name.length, cookie.length);

            }
        }
        return ""
    };

    private _cookieExists = (name: string): boolean => {
        let cookieVal = this._getCookie('organizationId');
        if (cookieVal != "") {
            return true;
        }
        return false;
    };

    private _hidePreviousButton = (section: OrderWizardPages, hide: boolean = true) => {
        let sectionElement = <HTMLElement>document.getElementById(section);
        let previousButton = sectionElement.getElementsByClassName('btnPagePrevious');
        if (previousButton.length > 0) {
            let prevBtn = previousButton[0];
            if (prevBtn) {
                if (hide) {
                    prevBtn.classList.add('wizardPage__element_hide');
                } else {
                    prevBtn.classList.remove('wizardPage__element_hide');
                }
            }
        }
    };

    private _changeSelectionButtonHandler = (targetPage: OrderWizardPages) => {
        if (targetPage == OrderWizardPages.CreateTenant) {
            const hide = this._previousSteps.length > 0;
            this._hidePreviousButton(OrderWizardPages.CreateTenant, hide);
            this._previousSteps.push(this._currentStep);
            this._currentStep = targetPage;
            this._navigateStep(this._currentStep);
        }
        if (targetPage === OrderWizardPages.ExistingTenant) {
            this._handle_change_tenant_click();
            return;
        }
        if (targetPage === OrderWizardPages.ExistingOrganization) {
            this._handle_change_organization_click();
            return;
        }
    };

    private _handle_change_tenant_click = () => {
        const orgaElement = document.getElementById(this._selectedOrganizationId);
        const elementDataSet = orgaElement.getAttribute('data-isAdmin');
        this._setBtnCreateTenantDisabled(!(elementDataSet === "true"));
        this._showTenantListForOrga(this._selectedOrganizationId);

        this._navigateStep(OrderWizardPages.ExistingTenant);
        this._hidePreviousButton(OrderWizardPages.AppSubscriptionSummary, false);
        this._nextButtonDisable(false, OrderWizardPages.AppSubscriptionSummary);

        const next = OrderWizardPages.ExistingTenant;
        this._previousSteps.push(this._currentStep);
        this._currentStep = next;
        this._navigateStep(next);
    };

    private _handle_change_organization_click = () => {
        this._hidePreviousButton(OrderWizardPages.AppSubscriptionSummary, false);
        this._nextButtonDisable(false, OrderWizardPages.AppSubscriptionSummary);

        const next = OrderWizardPages.ExistingOrganization;
        this._previousSteps.push(this._currentStep);
        this._currentStep = next;
        this._navigateStep(next);
    };

    private _handleScrollShadow = () => {
        const fixedFooters = Array.apply(null, document.querySelectorAll('.wizardPage__footer.fixed-on-scroll'));
        fixedFooters.forEach(footer => {
            if (this._elementFixedBottom(footer)) {
                footer.classList.add('wizardPage__footer_shadow');
            } else {
                footer.classList.remove('wizardPage__footer_shadow');
            }
        });
    }

    private _elementFixedBottom = (el: HTMLElement): boolean => {
        return (window.innerHeight - el.getBoundingClientRect().bottom) == 0
    };

    private logDebug(message: string) {
        if (this._debugEnabled) {
            console.log("[DEBUG]:", message);
        }
    }
}

interface GoogleTagManagerMapping {
    pagePath: string,
    pageTitle: string
}

enum OrderWizardPages {
    Current = '',
    ExistingOrganization = 'existingOrga',
    CreateOrganization = 'createOrga',
    ExistingTenant = 'existingTenant',
    CreateTenant = 'createTenant',
    AppSubscriptionSummary = 'appSubscriptionSummary',
    PrepairTenant = 'prepairTenant'
}